import { Button } from '~/design-system/elements/Button/Button';
import { DeckIcon } from '~/design-system/elements/DeckIcon/DeckIcon';

export interface ModelDropdownButtonUIProps {
  label?: string;
  isOpen?: boolean;
  onClick?: () => void;
  selectedId?: string | null;
  /** DeckIcon registry name; default Claude mark for production chat. */
  leadIconName?: string;
  leadIconPreserveColors?: boolean;
  /** Button size — defaults to `xs` (chat input bar). The slide toolbar passes `sm` to match its row. */
  size?: 'xs' | 'sm';
}

const resolveLeadIcon = (selectedId?: string | null): string => {
  if (!selectedId) return 'claude-logo';
  if (selectedId.startsWith('GEMINI_')) {
    return 'sparkling-2-fill';
  }
  if (selectedId === 'GPT_55') {
    return 'brand-chatgpt';
  }
  // OpenRouter-served models (DeepSeek/Qwen/Kimi) — generic AI sparkle, no per-provider mark exists.
  if (selectedId.startsWith('DEEPSEEK_') || selectedId.startsWith('QWEN_') || selectedId.startsWith('KIMI_')) {
    return 'sparkling-fill';
  }
  return 'claude-logo';
};

export const ModelDropdownButtonUI = ({
  label = 'Sonnet 4.6',
  isOpen = false,
  onClick,
  selectedId = 'GEMINI_38_FLASH',
  leadIconName,
  leadIconPreserveColors = true,
  size = 'xs',
}: ModelDropdownButtonUIProps) => {
  // Thinking variants drop the redundant word in favour of a small brain badge (matches the menu rows).
  const isThinking = !!selectedId && selectedId.endsWith('_THINKING');
  const displayLabel = isThinking ? label.replace(/ Thinking$/, '') : label;
  return (
    <Button
      type="button"
      variant="ghostMuted"
      size={size}
      onClick={onClick}
      aria-expanded={isOpen}
      aria-haspopup="listbox"
      leadIcon={
        <DeckIcon
          name={leadIconName ?? resolveLeadIcon(selectedId)}
          size={16}
          preserveColors={leadIconPreserveColors}
        />
      }
      tailIcon="arrow-down-s"
    >
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        {displayLabel}
        {isThinking && (
          <span style={{ display: 'inline-flex', color: 'var(--color-icon-default-muted)' }} aria-label="Thinking">
            <DeckIcon name="brain" size={12} />
          </span>
        )}
      </span>
    </Button>
  );
};
